import { Api_Paths } from "@/lib/api/paths";
import { Auth_Fetch } from "@/lib/auth/client/session";
import type {
  AmenitiesListResponse,
  BarrierFlagEntry,
  CheckoutRequest,
  CheckoutSession,
  GenerateReportsRequest,
  Job_Duration_Stats,
  ParcelPreview,
  PaymentRecord,
  ReportBundle,
  ReportJob,
} from "@/lib/parcelq/types";
import type { Booklet_Ready } from "@/lib/parcelq/booklet";

class ParcelQ_Client_Error extends Error {
  constructor(
    message: string,
    readonly Status: number,
  ) {
    super(message);
    this.name = "ParcelQ_Client_Error";
  }
}

async function ParcelQ_Fetch_Json<T>(Url: string, Init?: RequestInit): Promise<T> {
  const Request_Headers = new Headers(Init?.headers);
  if (Init?.body && !Request_Headers.has("Content-Type")) {
    Request_Headers.set("Content-Type", "application/json");
  }

  const Response = await Auth_Fetch(Url, {
    ...Init,
    headers: Request_Headers,
  });

  if (!Response.ok) {
    const Message = (await Response.text()).trim() || `Request failed (${Response.status})`;
    throw new ParcelQ_Client_Error(Message, Response.status);
  }

  return (await Response.json()) as T;
}

export const ParcelQ_Pending_Checkout_Key = "parcelq-pending-checkout";
export const ParcelQ_Pending_Search_Key = "parcelq-pending-search";

export type ParcelQ_Pending_Checkout = {
  apn: string;
  bundle: ReportBundle;
  tier_id: string;
  tier_title: string;
  radius_miles?: number;
};

export const ParcelQ_Client = {
  parcels: {
    async By_Apn(Apn: string, Signal?: AbortSignal): Promise<ParcelPreview> {
      const Params = new URLSearchParams({ apn: Apn });
      return ParcelQ_Fetch_Json<ParcelPreview>(`${Api_Paths.V1.Parcel.By_Apn}?${Params}`, {
        signal: Signal,
      });
    },
    async By_Address(Address: string, City?: string, Signal?: AbortSignal): Promise<ParcelPreview> {
      const Params = new URLSearchParams({ address: Address });
      if (City?.trim()) {
        Params.set("city", City.trim());
      }
      return ParcelQ_Fetch_Json<ParcelPreview>(`${Api_Paths.V1.Parcel.By_Address}?${Params}`, {
        signal: Signal,
      });
    },
  },

  amenities: {
    async List(Apn: string): Promise<AmenitiesListResponse> {
      const Params = new URLSearchParams({ apn: Apn });
      return ParcelQ_Fetch_Json<AmenitiesListResponse>(`${Api_Paths.V1.Amenities.List}?${Params}`);
    },
    async Barrier_Flags_Save(Apn: string, Flags: BarrierFlagEntry[]): Promise<void> {
      await ParcelQ_Fetch_Json<{ status: string }>(Api_Paths.V1.Amenities.Barrier_Flags, {
        method: "PUT",
        body: JSON.stringify({ apn: Apn, flags: Flags }),
      });
    },
  },

  payments: {
    async Config_Get(): Promise<{ stripe_enabled: boolean; payment_required: boolean }> {
      return ParcelQ_Fetch_Json(Api_Paths.V1.Payments.Config);
    },
    async Checkout_Create(Body: CheckoutRequest): Promise<CheckoutSession> {
      return ParcelQ_Fetch_Json<CheckoutSession>(Api_Paths.V1.Payments.Checkout, {
        method: "POST",
        body: JSON.stringify(Body),
      });
    },
    async Checkout_Confirm(Session_Id: string): Promise<PaymentRecord> {
      return ParcelQ_Fetch_Json<PaymentRecord>(Api_Paths.V1.Payments.Confirm, {
        method: "POST",
        body: JSON.stringify({ session_id: Session_Id }),
      });
    },
  },

  reports: {
    async Job_Generate(Body: GenerateReportsRequest, Signal?: AbortSignal): Promise<ReportJob> {
      return ParcelQ_Fetch_Json<ReportJob>(Api_Paths.V1.Reports.Generate, {
        method: "POST",
        body: JSON.stringify(Body),
        signal: Signal,
      });
    },
    async Job_Get(Job_Id: string): Promise<ReportJob> {
      return ParcelQ_Fetch_Json<ReportJob>(Api_Paths.V1.Reports.Job(Job_Id));
    },
    async Job_Delete(Job_Id: string): Promise<void> {
      await ParcelQ_Fetch_Json<{ ok: boolean }>(Api_Paths.V1.Reports.Job(Job_Id), { method: "DELETE" });
    },
    async Booklet_Load(Job_Id: string): Promise<Booklet_Ready> {
      return ParcelQ_Fetch_Json<Booklet_Ready>(Api_Paths.V1.Reports.Booklet(Job_Id));
    },
    async Stats_Load(): Promise<Job_Duration_Stats> {
      return ParcelQ_Fetch_Json<Job_Duration_Stats>(Api_Paths.V1.Reports.Stats);
    },
    async Job_Poll(
      Job_Id: string,
      On_Update?: (Job: ReportJob) => void,
      Interval_Ms = 2500,
      Signal?: AbortSignal,
    ): Promise<ReportJob> {
      while (true) {
        if (Signal?.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }
        const Job = await ParcelQ_Fetch_Json<ReportJob>(Api_Paths.V1.Reports.Job(Job_Id), {
          signal: Signal,
        });
        On_Update?.(Job);
        if (Job.status === "completed" || Job.status === "failed") {
          return Job;
        }
        await new Promise<void>((Resolve, Reject) => {
          if (Signal?.aborted) {
            Reject(new DOMException("Aborted", "AbortError"));
            return;
          }

          let Timer = 0;
          const Abort_Handle = () => {
            window.clearTimeout(Timer);
            Reject(new DOMException("Aborted", "AbortError"));
          };

          Timer = window.setTimeout(() => {
            Signal?.removeEventListener("abort", Abort_Handle);
            Resolve();
          }, Interval_Ms);

          Signal?.addEventListener("abort", Abort_Handle, { once: true });
        });
      }
    },
    Download_Url(Report_Type: string, Apn: string, Job_Id?: string): string {
      const Base = Api_Paths.V1.Reports.Download(Report_Type, Apn);
      if (!Job_Id) {
        return Base;
      }
      const Params = new URLSearchParams({ job_id: Job_Id });
      return `${Base}?${Params}`;
    },
    Console_Log_Url(Job_Id: string): string {
      return Api_Paths.V1.Reports.Job_Console_Log(Job_Id);
    },
  },
};

export function Error_Is_Abort(Caught: unknown): boolean {
  return (
    (Caught instanceof DOMException && Caught.name === "AbortError") ||
    (Caught instanceof Error && Caught.name === "AbortError")
  );
}

export function Query_Parse_Apn_Or_Address(Query_Text: string): { apn?: string; address?: string } {
  const Query_Trimmed = Query_Text.trim();
  const Query_Normalized = Query_Trimmed.replace(/\s/g, "");
  const Apn_Pattern = /^\d{3,}-\d{2,}-\d{2,}$/;

  if (Apn_Pattern.test(Query_Normalized)) {
    return { apn: Query_Normalized };
  }

  return { address: Query_Trimmed };
}

export function Tier_Id_To_Bundle(Tier_Id: string): ReportBundle {
  switch (Tier_Id) {
    case "radius-amenities":
      return "amenities";
    case "feasibility":
      return "feasibility";
    default:
      return "complete";
  }
}

export function Bundle_To_Tier_Meta(Bundle: ReportBundle): {
  Id: string;
  Title: string;
  Reports: string;
  Price: number;
  Credits: number;
  Hours_Saved: number;
} {
  switch (Bundle) {
    case "amenities":
      return {
        Id: "radius-amenities",
        Title: "Amenity Report + Radius Map",
        Reports: "Radius Map · Amenities Detail · Amenities Summary",
        Price: 30,
        Credits: 1,
        Hours_Saved: 2,
      };
    case "feasibility":
      return {
        Id: "feasibility",
        Title: "TTT Report",
        Reports: "Public Due Diligence · Automatic Red Flags",
        Price: 45,
        Credits: 2,
        Hours_Saved: 4,
      };
    default:
      return {
        Id: "combo",
        Title: "Combination Report",
        Reports: "TTT Report · Amenity Report · Radius Map",
        Price: 70,
        Credits: 4,
        Hours_Saved: 6,
      };
  }
}
